/**
 * v4.1 Phase 36.9 — Manage Members dialog.
 *
 * Two-tab surface for a project owner:
 *   - Members: list, change role, remove, transfer ownership
 *   - Pending invites: list email invitations awaiting sign-up, revoke
 *
 * Viewer / editor members get a read-only variant with just the list;
 * they can see who else is on the project but can't mutate.
 *
 * Gated client-side by COLLAB_MODE_ENABLED. Backend gating is the
 * source of truth; the frontend flag just hides affordances.
 */

import { useMemo, useState } from "react";
import Modal from "../Modal.jsx";
import { useStore } from "../../store/useStore.js";
import { getUserInfo } from "../../services/auth.js";
import "./ManageMembersDialog.css";

const ROLE_LABELS = { owner: "Owner", editor: "Editor", viewer: "Viewer" };

/**
 * Display a user-friendly label for a Cognito sub. When the sub belongs
 * to the viewer we show their email (from the ID token); otherwise we
 * shorten the sub to first-4 + last-4 with an ellipsis. A tenant users
 * directory (tech-debt G-pending) would let us show real names here.
 */
function displayNameForSub(sub, callerInfo) {
  if (!sub) return "—";
  if (callerInfo?.sub === sub && callerInfo?.email) {
    return `${callerInfo.email} (you)`;
  }
  if (sub.length <= 10) return sub;
  return `${sub.slice(0, 4)}…${sub.slice(-4)}`;
}

function RoleBadge({ role }) {
  return (
    <span className={`member-role-badge member-role-${role}`}>
      {ROLE_LABELS[role] || role}
    </span>
  );
}

function memberInitials(userId) {
  // Cognito sub is a UUID with no human name. Use first two chars for
  // initials so the avatar stack stays dense without fetching the
  // tenant user directory on every render. The Manage Members dialog
  // (Phase 36.9+ frontend) could later wire this to a real lookup.
  if (!userId) return "?";
  return userId.slice(0, 2).toUpperCase();
}

function MemberRow({
  member,
  canManage,
  onChangeRole,
  onRemove,
  onTransferOwnership,
  disabled,
  callerInfo,
}) {
  const label = displayNameForSub(member.userId, callerInfo);
  return (
    <li className="member-row" aria-label={`Member ${label}`}>
      <div className="member-avatar" aria-hidden="true">
        {memberInitials(member.userId)}
      </div>
      <div className="member-info">
        <div className="member-id">{label}</div>
        <RoleBadge role={member.role} />
      </div>
      {canManage && (
        <div className="member-actions">
          <select
            aria-label="Change role"
            value={member.role}
            onChange={(e) => onChangeRole(member.userId, e.target.value)}
            disabled={disabled}
          >
            <option value="editor">Editor</option>
            <option value="viewer">Viewer</option>
          </select>
          <button
            type="button"
            className="btn-link-danger"
            onClick={() => onRemove(member.userId)}
            disabled={disabled}
          >
            Remove
          </button>
          <button
            type="button"
            className="btn-link"
            onClick={() => onTransferOwnership(member.userId)}
            disabled={disabled}
          >
            Make owner
          </button>
        </div>
      )}
    </li>
  );
}

function AddMemberForm({ onAddUserId, onInviteEmail, disabled }) {
  const [mode, setMode] = useState("userId"); // "userId" | "email"
  const [identifier, setIdentifier] = useState("");
  const [role, setRole] = useState("viewer");
  const [localError, setLocalError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLocalError(null);
    const trimmed = identifier.trim();
    if (!trimmed) {
      setLocalError("Enter a user ID or email");
      return;
    }
    try {
      if (mode === "userId") {
        await onAddUserId(trimmed, role);
      } else {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
          setLocalError("Enter a valid email address");
          return;
        }
        await onInviteEmail(trimmed, role);
      }
      setIdentifier("");
    } catch (err) {
      setLocalError(err.message || "Failed to add member");
    }
  };

  return (
    <form className="member-add-form" onSubmit={submit}>
      <div className="member-add-mode" role="radiogroup" aria-label="Add mode">
        <label className={mode === "userId" ? "selected" : ""}>
          <input
            type="radio"
            name="addMode"
            value="userId"
            checked={mode === "userId"}
            onChange={() => setMode("userId")}
          />{" "}
          Existing user
        </label>
        <label className={mode === "email" ? "selected" : ""}>
          <input
            type="radio"
            name="addMode"
            value="email"
            checked={mode === "email"}
            onChange={() => setMode("email")}
          />{" "}
          Invite by email
        </label>
      </div>
      <div className="member-add-fields">
        <input
          type={mode === "email" ? "email" : "text"}
          className="member-add-identifier"
          placeholder={mode === "email" ? "name@company.com" : "User sub"}
          value={identifier}
          onChange={(e) => setIdentifier(e.target.value)}
          disabled={disabled}
          aria-label={mode === "email" ? "Invitee email" : "User ID"}
        />
        <select
          className="member-add-role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={disabled}
          aria-label="Role"
        >
          <option value="editor">Editor</option>
          <option value="viewer">Viewer</option>
        </select>
        <button type="submit" className="btn-primary" disabled={disabled}>
          {mode === "email" ? "Send invite" : "Add"}
        </button>
      </div>
      {localError && <p className="member-form-error">{localError}</p>}
    </form>
  );
}

function TransferOwnershipConfirm({ targetUserId, onConfirm, onCancel, busy }) {
  return (
    <div className="transfer-confirm">
      <p>
        Transfer ownership to <strong>{targetUserId}</strong>? You&apos;ll
        become an <strong>editor</strong> on this project. This action
        cannot be reversed automatically — only the new owner can transfer
        it back.
      </p>
      <div className="transfer-confirm-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn-primary-danger"
          onClick={onConfirm}
          disabled={busy}
          autoFocus
        >
          {busy ? "Transferring…" : "Transfer ownership"}
        </button>
      </div>
    </div>
  );
}

export default function ManageMembersDialog({
  project,
  open,
  onClose,
  onOwnershipTransferred,
  role,
}) {
  const pushToast = useStore((s) => s.pushToast);
  const addProjectMember = useStore((s) => s.addProjectMember);
  const removeProjectMember = useStore((s) => s.removeProjectMember);
  const updateProjectMemberRole = useStore(
    (s) => s.updateProjectMemberRole,
  );
  const transferProjectOwnership = useStore(
    (s) => s.transferProjectOwnership,
  );
  const revokeProjectInvite = useStore((s) => s.revokeProjectInvite);

  const [tab, setTab] = useState("members"); // "members" | "invites"
  const [pendingInvites, setPendingInvites] = useState([]);
  const [busy, setBusy] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);
  const callerInfo = useMemo(() => getUserInfo(), []);

  const canManage = role === "owner";
  const members = project?.members || [];

  const handleAddUserId = async (userId, memberRole) => {
    setBusy(true);
    try {
      await addProjectMember(project.id, { userId, role: memberRole });
      pushToast("Member added", "success");
    } finally {
      setBusy(false);
    }
  };

  const handleInviteEmail = async (email, memberRole) => {
    setBusy(true);
    try {
      const result = await addProjectMember(project.id, {
        email,
        role: memberRole,
      });
      if (result?.pending) {
        setPendingInvites((prev) => [
          ...prev.filter((p) => p.email !== result.email),
          result,
        ]);
        pushToast(`Invite sent to ${email}`, "success");
      } else {
        pushToast("Member added", "success");
      }
    } finally {
      setBusy(false);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    setBusy(true);
    try {
      await updateProjectMemberRole(project.id, userId, newRole);
      pushToast("Role updated", "success");
    } catch (err) {
      pushToast(err.message || "Failed to update role", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (userId) => {
    setBusy(true);
    try {
      await removeProjectMember(project.id, userId);
      pushToast("Member removed", "success");
    } catch (err) {
      pushToast(err.message || "Failed to remove member", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmTransfer = async () => {
    if (!transferTarget) return;
    setBusy(true);
    try {
      await transferProjectOwnership(project.id, transferTarget);
      pushToast("Ownership transferred", "success");
      setTransferTarget(null);
      onOwnershipTransferred?.();
      onClose();
    } catch (err) {
      pushToast(err.message || "Failed to transfer ownership", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeInvite = async (email) => {
    setBusy(true);
    try {
      await revokeProjectInvite(project.id, email);
      setPendingInvites((prev) => prev.filter((p) => p.email !== email));
      pushToast("Invite revoked", "success");
    } catch (err) {
      pushToast(err.message || "Failed to revoke invite", "error");
    } finally {
      setBusy(false);
    }
  };

  if (!project) return null;

  return (
    <Modal open={open} onClose={onClose} title="Project members" wide>
      {transferTarget ? (
        <TransferOwnershipConfirm
          targetUserId={displayNameForSub(transferTarget, callerInfo)}
          onConfirm={handleConfirmTransfer}
          onCancel={() => setTransferTarget(null)}
          busy={busy}
        />
      ) : (
        <div className="manage-members">
          <div className="manage-members-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={tab === "members"}
              className={tab === "members" ? "tab-active" : ""}
              onClick={() => setTab("members")}
            >
              Members ({members.length})
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === "invites"}
              className={tab === "invites" ? "tab-active" : ""}
              onClick={() => setTab("invites")}
            >
              Pending invites ({pendingInvites.length})
            </button>
          </div>
          {tab === "members" && (
            <div className="tab-panel" role="tabpanel">
              <div className="owner-row">
                <div className="member-avatar" aria-hidden="true">
                  {memberInitials(project.ownerId)}
                </div>
                <div className="member-info">
                  <div className="member-id">
                    {displayNameForSub(project.ownerId, callerInfo)}
                  </div>
                  <RoleBadge role="owner" />
                </div>
              </div>
              {members.length === 0 ? (
                <p className="empty-hint">
                  No members yet. {canManage && "Add the first one below."}
                </p>
              ) : (
                <ul className="member-list">
                  {members.map((m) => (
                    <MemberRow
                      key={m.userId}
                      member={m}
                      canManage={canManage}
                      callerInfo={callerInfo}
                      onChangeRole={handleChangeRole}
                      onRemove={handleRemove}
                      onTransferOwnership={(userId) =>
                        setTransferTarget(userId)
                      }
                      disabled={busy}
                    />
                  ))}
                </ul>
              )}
              {canManage && (
                <AddMemberForm
                  onAddUserId={handleAddUserId}
                  onInviteEmail={handleInviteEmail}
                  disabled={busy}
                />
              )}
            </div>
          )}
          {tab === "invites" && (
            <div className="tab-panel" role="tabpanel">
              {pendingInvites.length === 0 ? (
                <p className="empty-hint">
                  No pending invites.{" "}
                  {canManage && "Invite someone by email from the Members tab."}
                </p>
              ) : (
                <ul className="invite-list">
                  {pendingInvites.map((inv) => (
                    <li key={inv.email} className="invite-row">
                      <div className="member-info">
                        <div className="member-id">{inv.email}</div>
                        <RoleBadge role={inv.role} />
                      </div>
                      {canManage && (
                        <button
                          type="button"
                          className="btn-link-danger"
                          onClick={() => handleRevokeInvite(inv.email)}
                          disabled={busy}
                        >
                          Revoke
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
